class WebSocketManager {
    constructor() {
        if (!WebSocketManager.instance) {
            this.ws = null;
            this.listeners = {}; // Store listeners for specific request IDs
            this.pendingRequests = {}; // Store pending requests by req_id
            this.requestQueue = []; // Queue for requests when WebSocket is not ready
            this.reconnectAttempts = 0;
            this.maxReconnectAttempts = 10;
            this.reconnectDelay = 1000;
            this.status = 'disconnected';
            this.instance_id = Math.random(); // Random ID to identify the instance
            this.connect();
            WebSocketManager.instance = this;
        }

        return WebSocketManager.instance;
    }

    static getInstance() {
        if (!WebSocketManager.instance) {
            WebSocketManager.instance = new WebSocketManager();
        }
        return WebSocketManager.instance;
    }

    connect() {
        return new Promise((resolve) => {
            if (this.status === 'authed') {
                console.log('WebSocket already authenticated');
                resolve(true);
                return;
            }

            if (this.ws && this.ws.readyState === WebSocket.OPEN) {
                console.log('WebSocket already connected');
                resolve(true);
                return;
            }

            const webSocketURL = process.env.VUE_APP_PUBLIC_WS_URL  || 'ws://localhost:3000/public';
            console.log('WebSocketURL:', webSocketURL);

            this.ws = new WebSocket(webSocketURL);
            this.status = 'connecting';

            this.ws.onopen = () => {
                console.log('WebSocket connected', this.ws.readyState);
                this.status = 'connected';
                this.status = 'authed';
                this.processQueue(); // Process the queue once the WebSocket is open
                resolve(true);
            };

            this.ws.onmessage = (event) => {
                const data = JSON.parse(event.data);
                console.log("Received message:", data);

                if (data.welcome) {
                    console.log('Server welcome:', data.welcome, this.ws.readyState);
                    this.status = 'authed';
                    this.reconnectAttempts = 0;
                    resolve(true);
                }

                if (data.req_id && this.pendingRequests[data.req_id]) {
                    this.pendingRequests[data.req_id](data);
                    delete this.pendingRequests[data.req_id];
                } else {
                    console.warn("Received message without a matching req_id:", data);
                }
            };

            this.ws.onclose = () => {
                console.log('WebSocket closed');
                this.status = 'disconnected';
                this.tryReconnect();
            };

            this.ws.onerror = (error) => {
                console.error('WebSocket Error:', error);
                this.status = 'disconnected';
                this.tryReconnect();
            };
        });
    }

    tryReconnect() {
        if (this.reconnectAttempts < this.maxReconnectAttempts) {
            const delay = this.reconnectDelay * Math.pow(2, this.reconnectAttempts);
            console.log(`Attempting to reconnect... (Attempt ${this.reconnectAttempts + 1}, delay: ${delay}ms)`);
            this.reconnectAttempts += 1;

            setTimeout(() => {
                this.connect().then((connected) => {
                    if (connected) {
                        console.log('Reconnected to WebSocket');
                        this.reconnectAttempts = 0; // Reset reconnect attempts after successful connection
                    }
                });
            }, delay);
        } else {
            console.error('Maximum reconnection attempts reached.');
        }
    }

    async request(entity, action, payload, timeoutDuration = 15000) {
        console.log('request:', entity, action, payload);
        // await this.connect();

        return new Promise((resolve, reject) => {
            const req_id = this.generateReqId();
            const message = JSON.stringify({ req_id, entity, action, payload });

            const timeout = setTimeout(() => {
                delete this.pendingRequests[req_id];
                reject(new Error(`Request timed out after ${timeoutDuration}ms`));
            }, timeoutDuration);

            this.pendingRequests[req_id] = (data) => {
                clearTimeout(timeout);
                resolve(data);
            };

            if (this.ws.readyState === WebSocket.OPEN) {
                this.ws.send(message);
            } else {
                console.log('WebSocket not open, queuing request');
                this.requestQueue.push(message);
            }
        });
    }

    processQueue() {
        while (this.requestQueue.length > 0 && this.ws.readyState === WebSocket.OPEN) {
            const message = this.requestQueue.shift();
            console.log('Processing queued request:', message);
            this.ws.send(message);
        }
    }

    generateReqId() {
        return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    }
}

export default WebSocketManager;
